import { CommonModule, DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  ViewChild,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterModule } from '@angular/router';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { IconsModule } from '@progress/kendo-angular-icons';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { parseNumber } from '@progress/kendo-angular-intl';
import { DrawerSelectEvent, LayoutModule } from '@progress/kendo-angular-layout';
import { NavigationModule } from '@progress/kendo-angular-navigation';
import { PopupRef, PopupService } from '@progress/kendo-angular-popup';
import { KENDO_RIPPLE } from '@progress/kendo-angular-ripple';
import {
  SVGIcon,
  arrowLeftIcon,
  bellIcon,
  chevronDoubleLeftIcon,
  chevronDoubleRightIcon,
  chevronDownIcon,
  gearsIcon,
  infoCircleIcon,
  menuIcon,
  userIcon,
  xIcon,
} from '@progress/kendo-svg-icons';
import { catchError, filter, of, takeUntil } from 'rxjs';
import { OrgPickerComponent } from '../org-picker/org-picker.component';
import { Organization } from '../../models/entities';
import { User } from '../../models/user';
import { EventSourceService } from '../../services/event-source.service';
import { EventService, Events } from '../../services/event.service';
import { LogoutService } from '../../services/logout.service';
import { BaseComponent } from '../../shared/common/base.component';
import { WhiteLabelService } from '../../../features/admin/organizations/components/white-label/services/white-label.service';
import { OrganizationService } from '../../../features/admin/organizations/services/organization.service';
import { UserService } from '../../../features/admin/users/services/user.service';
import { identifyOrganizationPerRequest } from '../../models/organizations';
import { CustomerFeatureFlagsService } from '../../services/feature-flags.service';
import { NonceService } from '../../../features/customer/layout/nonce.service';
import { SupportWidgetService } from '../../../features/customer/layout/services/support-widget.service';
import { NotificationsComponent } from './components/notifications/notifications.component';
import { VipSupportComponent } from './components/vip-support/vip-support.component';
import { ExtendedDrawerItem, LAYOUT_CONFIG } from './layout-config';
import { lastWorkspaceUrl, rememberWorkspaceUrl } from './workspaces';
import { isCyflareAdmin } from '../../shared/cyflare-admin';
import { PartnerAccessService } from '../../services/partner-portal/partner-access.service';
import { CasiChatStore } from '../../../features/admin/casi-chat/services/casi-chat.store';
import { CasiChatPanelComponent } from '../../../features/admin/casi-chat/components/chat-panel/chat-panel.component';
import { CasiSideTabComponent } from '../../../features/admin/casi-chat/components/side-tab/side-tab.component';
import { environment } from '../../../../environments/environment';

type SelectionResult = {
  items: ExtendedDrawerItem[];
  settingsActive: boolean;
};

function applySelectionState(
  items: ExtendedDrawerItem[],
  url: string,
  settingsRoute: string | null | undefined
): SelectionResult {
  const path = url.split('?')[0];

  if (settingsRoute && path === settingsRoute) {
    return {
      items: items.map((item) => ({ ...item, selected: false })),
      settingsActive: true,
    };
  }

  const index = items.findIndex((item) => item.path === path);
  if (index === -1) {
    return { items, settingsActive: false };
  }

  return {
    items: items.map((item, i) => ({ ...item, selected: i === index })),
    settingsActive: false,
  };
}

/**
 * Query params that describe the SESSION rather than a page, and so survive a
 * navigation. Everything else belongs to the page that wrote it.
 *
 * Deliberately an allowlist. The previous denylist meant any param a page added
 * to its own URL followed the user to every other page until someone
 * remembered to exclude it by name here.
 */
const GLOBAL_QUERY_PARAMS = ['organizationId', 'account_type'] as const;

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-layout-shell',
  standalone: true,
  imports: [
    DialogsModule,
    KENDO_RIPPLE,
    CommonModule,
    RouterModule,
    LayoutModule,
    ButtonsModule,
    IconsModule,
    NavigationModule,
    IndicatorsModule,
    OrgPickerComponent,
    CasiChatPanelComponent,
    CasiSideTabComponent,
  ],
  templateUrl: './app-layout.component.html',
  styleUrl: './app-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UserService, OrganizationService, NonceService, SupportWidgetService],
})
export class AppLayoutComponent extends BaseComponent implements AfterViewInit {
  public readonly config = inject(LAYOUT_CONFIG);

  @ViewChild('anchor', { static: false }) anchor!: ElementRef;
  @ViewChild('drawer') drawer!: any;

  private readonly user = inject(User);
  private readonly popupService = inject(PopupService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly orgService = inject(OrganizationService);
  public logoutService = inject(LogoutService);
  public eventSourceService = inject(EventSourceService);
  public eventService = inject(EventService);
  public readonly whitelabel = inject(WhiteLabelService);
  public featureFlagsService = inject(CustomerFeatureFlagsService);
  private readonly supportWidgetService = inject(SupportWidgetService);
  private readonly partnerAccessService = inject(PartnerAccessService);

  // Icons
  public chevronDoubleLeftIcon: SVGIcon = chevronDoubleLeftIcon;
  public chevronDoubleRightIcon: SVGIcon = chevronDoubleRightIcon;
  public bellIcon: SVGIcon = bellIcon;
  public xIcon: SVGIcon = xIcon;
  public gearsIcon: SVGIcon = gearsIcon;
  public chevronDownIcon: SVGIcon = chevronDownIcon;
  public infoCircleIcon: SVGIcon = infoCircleIcon;
  public arrowLeftIcon: SVGIcon = arrowLeftIcon;
  public userIcon: SVGIcon = userIcon;
  public menuIcon: SVGIcon = menuIcon;

  // CASI chat slideout (internal Cyflare admins; CASI only accepts prod sign-ins)
  public readonly casi = inject(CasiChatStore);
  public get showCasi(): boolean {
    return environment.casiEnabled && isCyflareAdmin(this.user);
  }

  // Signals & state
  public currentOrg = signal<any>(null);
  public drawerExpanded = signal<boolean>(true);
  public settingsActive = signal<boolean>(false);
  public notifications = signal<any[]>([]);
  public orgs = signal<{ id: number; name: string; account_type: number }[]>([]);

  avatarText: string | null = null;
  private popupRef: PopupRef | undefined;
  bodyElement: ElementRef;
  public primaryId: number | null | undefined;

  // Navigation — signal so feature flags can rebuild it reactively
  public Items = signal<ExtendedDrawerItem[]>([]);
  public data: Array<any> = [];
  public selected: ExtendedDrawerItem | undefined;
  public expandedIndices = [1, 2];
  public isItemExpanded = (item: any): boolean => {
    return this.expandedIndices.indexOf(item.id) >= 0;
  };

  public navigateToSettings(): void {
    if (!this.config.settingsRoute) return;

    let currentQueryParams = { ...this.route.snapshot.queryParams };

    if ('advisoryId' in currentQueryParams) {
      delete currentQueryParams['advisoryId'];
    }
    if ('from_email' in currentQueryParams) {
      delete currentQueryParams['from_email'];
    }

    this.router.navigate([this.config.settingsRoute], {
      queryParams: currentQueryParams,
    });
  }

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    super();
    this.whitelabel.whiteLabelEffect();
    this.initEffect();
    this.setUserAvatarText();

    void this.partnerAccessService.load();

    // Build initial nav and user dropdown from config
    this.Items.set(this.config.menuItemsFactory(this.user));
    this.data = this.config.userMenuItemsFactory(this.user);

    // Handle scripts via config or support widget service
    if (this.config.enableSupportWidget) {
      this.supportWidgetService.initialize();
    } else {
      if (this.config.scripts.removeChatWidget) {
        this.removeChatWidget();
      }
    }

    // Set up feature-flag-driven nav rebuilding if enabled
    if (this.config.enableFeatureFlags) {
      // Invalidate cached flags so we fetch fresh data for this layout context
      this.featureFlagsService.invalidateCache();
      this.initNavFlagsEffect();
    }

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter((event: NavigationEnd) => !event.urlAfterRedirects.includes('auth'))
      )
      .subscribe((event: NavigationEnd) => {
        if (this.config.workspace) rememberWorkspaceUrl(this.config.workspace, event.urlAfterRedirects);
        const { items, settingsActive } = applySelectionState(
          this.Items(),
          event.urlAfterRedirects,
          this.config.settingsRoute
        );
        this.Items.set(items);
        this.settingsActive.set(settingsActive);
      });
    this.bodyElement = new ElementRef(this.document.body);
  }

  initEffect() {
    effect(
      () => {
        this.eventSourceService.connectToServerSentEvents(['message']).subscribe({
          next: (response: any) => {
            const data = JSON.parse(response.data);
            if (data.id) {
              this.notifications.update((x) => [...x, data]);
            }
          },
          error: (error) => {
            console.log(error);
          },
        });
        this.primaryId = this.user.primary_organization?.id;
      },
      { allowSignalWrites: true }
    );
  }

  /**
   * Rebuild nav items when feature flags load or change.
   * Redirects away from flag-gated routes if the flag is now disabled.
   * Only called when config.enableFeatureFlags is true.
   */
  initNavFlagsEffect(): void {
    effect(
      () => {
        const flagsData = this.featureFlagsService.flags();
        const flags = flagsData?.flags ?? {};

        const fresh = this.config.menuItemsFactory(this.user, flags);
        const { items, settingsActive } = applySelectionState(fresh, this.router.url, this.config.settingsRoute);
        this.Items.set(items);
        this.settingsActive.set(settingsActive);

        // If the current route requires a flag that is now disabled, redirect.
        // Skip while flags are still loading (e.g. mid org switch) — otherwise the
        // null state would be read as "flag missing" and bump the user off gated pages.
        if (flagsData && this.config.flagGatedRoutes) {
          const currentPath = this.router.url.split('?')[0];
          const gatedRoute = this.config.flagGatedRoutes.find((r) => currentPath.startsWith(r.pathPrefix));
          const defaultRedirect = this.config.defaultRedirectRoute ?? '/customer/customer-dashboard';
          if (gatedRoute && flags[gatedRoute.flag] !== true) {
            this.router.navigate([defaultRedirect], { queryParamsHandling: 'preserve' });
          }
        }
      },
      { allowSignalWrites: true }
    );
  }

  public showVipSupport = computed(() => {
    if (this.config.enableFeatureFlags) {
      // Read the flags signal so Angular tracks this dependency
      const flagsData = this.featureFlagsService.flags();
      return flagsData?.flags?.['vip_support'] === true;
    }
    return false;
  });

  ngAfterViewInit() {
    this.getOrganization();
  }

  private setUserAvatarText() {
    const user = this.user;
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';

    if (firstName && lastName) {
      this.avatarText = `${firstName.charAt(0)}${lastName.charAt(0)}`;
    } else {
      this.avatarText = null;
    }
  }

  /** Open the CASI slideout on the org being viewed, or close it. */
  public toggleCasi(): void {
    if (this.casi.panelOpen()) this.casi.closePanel();
    else this.casi.openPanel(this.currentOrg());
  }

  public handlePopup(component: 'notifications' | 'vipSupport'): void {
    this.togglePopup(component);
  }

  public togglePopup(componentType: 'notifications' | 'vipSupport' = 'notifications'): void {
    if (this.popupRef) {
      this.popupRef.close();
      this.popupRef = undefined;
    } else {
      const componentToOpen = componentType === 'notifications' ? NotificationsComponent : VipSupportComponent;

      this.popupRef = this.popupService.open({
        anchor: this.bodyElement,
        content: componentToOpen,
        animate: { type: 'slide', direction: 'left', duration: 200 },
        anchorAlign: { horizontal: 'right', vertical: 'top' },
        popupAlign: { horizontal: 'right', vertical: 'top' },
      });

      if (componentType === 'notifications') {
        const notificationsComponent = this.popupRef.content.instance as NotificationsComponent;
        notificationsComponent.notifications.set(this.notifications());
        notificationsComponent.close.subscribe(() => {
          this.notifications.set(notificationsComponent.notifications());
          this.togglePopup();
        });
      } else if (componentType === 'vipSupport') {
        const vipSupportComponent = this.popupRef.content.instance as VipSupportComponent;
        vipSupportComponent.close.subscribe(() => {
          this.togglePopup();
        });
      }
    }
  }

  public onUserProfileClick(event: any): void {
    if (event.text === 'Logout') {
      this.logoutService
        .globalSignOut(this.user.id)
        .pipe(
          takeUntil(this.destroyed$),
          catchError((error) => {
            console.error('Error during logout', error);
            return of(null);
          })
        )
        .subscribe(() => {
          this.user.clearUserData();
          this.router.navigate(['/auth/login']);
        });
    } else if (this.config.onUserProfileAction) {
      this.config.onUserProfileAction(event.text, this.router);
    }
  }

  onItemClick(e: any): void {
    const currentQueryParams = this.route.snapshot.queryParams;
    if (e.external) {
      window.open(e.url, '_blank');
    } else {
      this.router.navigate([e.url], { queryParams: currentQueryParams });
    }
  }

  private getOrganization() {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      let orgId = 0;
      const hasOrgIdInUrl = !!params['organizationId'];
      const hasAccountTypeInUrl = !!params['account_type'];
      const hasPrimaryOrgIdInUrl = !!params['primary_organization_id'];

      if (hasOrgIdInUrl) {
        orgId = parseNumber(params['organizationId']);
      } else if (hasPrimaryOrgIdInUrl) {
        orgId = parseNumber(params['primary_organization_id']);
      } else {
        orgId = this.user.primary_organization?.id!;
      }

      identifyOrganizationPerRequest(orgId, this.user);
      this.orgService
        .getOrganizationById(orgId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((org) => {
          this.currentOrg.set({ id: org.id, display_text: org.name });
          this.user.selected_organization = new Organization(org);
          this.eventService.emit(Events.OrgDropdownChange.type, new Events.OrgDropdownChange(org));

          // Load feature flags for the selected org if enabled
          if (this.config.enableFeatureFlags) {
            this.featureFlagsService.loadFlags(org.id!);
          }

          if (!hasOrgIdInUrl || !hasAccountTypeInUrl) {
            const queryParams: { organizationId?: number; account_type?: number } = {};
            if (!hasOrgIdInUrl) {
              queryParams.organizationId = org.id!;
            }
            if (!hasAccountTypeInUrl && org.account_type != null) {
              queryParams.account_type = org.account_type;
            }
            if (Object.keys(queryParams).length > 0) {
              this.router.navigate([], {
                relativeTo: this.router.routerState.root,
                queryParams,
                queryParamsHandling: 'merge',
              });
            }
          }
        });
    });
  }

  onOrgDropdownChanged($event: any) {
    if (!$event) return;
    const orgPayload = {
      id: $event.id,
      name: $event.display_text,
      account_type: $event.account_type,
    };
    this.config.onOrgChange($event, this.router, this.route);
    this.eventService.emit(Events.OrgDropdownChange.type, new Events.OrgDropdownChange(orgPayload as any));
  }

  public onSelect(ev: DrawerSelectEvent): void {
    const item = ev.item as ExtendedDrawerItem;
    const current = ev.item.id;

    /**
     * Carry only the APP-WIDE params across a page change.
     *
     * This used to copy every query param on the current URL and delete two by
     * name (`advisoryId`, `from_email`). A denylist loses by construction: every
     * page that adds a param to its own URL has to remember to come back here
     * and exclude it, and none ever do — so page state followed the user
     * everywhere. Navigating away from the Visualizer carried its
     * `kind/id/label/group/tag/classification/return` onto Cases, Reporting and
     * everything else, where they mean nothing and pollute any link the user
     * then copies.
     *
     * `organizationId` and `account_type` are the only genuinely global ones —
     * which the fallback branch below already asserted by rebuilding exactly
     * those two. Everything else belongs to the page that wrote it and should
     * die with it.
     */
    const snapshotParams = this.route.snapshot.queryParams;
    const currentQueryParams: Params = {};
    for (const key of GLOBAL_QUERY_PARAMS) {
      if (snapshotParams[key] != null) currentQueryParams[key] = snapshotParams[key];
    }

    if (this.expandedIndices.indexOf(current) >= 0) {
      this.expandedIndices = this.expandedIndices.filter((id) => id !== current);
    } else {
      this.expandedIndices.push(current);
    }

    if (!item.path) {
      return;
    }

    if (item.external) {
      window.open(item.path, '_blank');
    } else if (item.path) {
      if ('organizationId' in currentQueryParams) {
        this.router.navigate([item.path], { queryParams: currentQueryParams });
      } else {
        const fallbackQueryParams = {
          organizationId: this.user.primary_organization?.id,
          account_type: this.user.primary_organization?.account_type,
        };
        this.router.navigate([item.path], { queryParams: fallbackQueryParams });
      }
    }

    this.settingsActive.set(false);
    this.Items.update((items) =>
      items.map((i) => ({
        ...i,
        selected: i === ev.item,
      }))
    );
    this.selected = ev.item.text;
  }

  removeChatWidget(): void {
    const chatWidget = document.getElementById('zohohc-asap-web-helper-main');
    if (chatWidget) {
      chatWidget.remove();
    }
  }

  /** Null when the user has nowhere to return to. */
  public readonly sandboxReturn = (() => {
    const target = this.config.sandbox?.returnTo;
    return target && target.available(this.user) ? target : null;
  })();

  public returnFromSandbox(): void {
    const target = this.sandboxReturn;
    if (!target) return;
    this.router.navigateByUrl(lastWorkspaceUrl(target.workspace) ?? target.home(this.user));
  }

  public toggleDrawer(): void {
    this.drawerExpanded.update((state) => !state);
  }
}
